import { RefObject, useState } from 'react';
import { Toast } from 'primereact/toast';
import { EMPTY_FORM, ExposureForm, StudentRow } from './types';
import { buildEditForm, buildSavePayload } from './editFormMapper';
import { useUI } from '../../../shared/UI/Uicontext';
import { useExposureUploads } from './useExposureUploads';
import { useExposureDialogs } from './useExposureDialogs';
import { useRemoveStudent } from './useRemoveStudent';

export function useExposureActions(
  data: ReturnType<typeof import('./useExposureData').useExposureData>,
  toastRef: RefObject<Toast | null>
) {
  const {
    schoolId,
    session,
    studentJsonData,
    removeStudentLocally,
    districtList,
    setDistrictList,
    collegeMaster,
    setCollegeOptions,
    allClassSectionData,
    getGroupName,
    refetchExposure,
    postExposureVisit,
    addExposureStudent,
    postExposureUploads,
    savedUploads,
  } = data;

  const { showLoader, hideLoader } = useUI();

  type Sev = 'success' | 'info' | 'warn' | 'error';
  const notify = (severity: Sev, summary: string, detail: string) =>
    toastRef.current?.show({ severity, summary, detail });
  const savePayloadMeta = {
    user_type: session.usertype ?? session.usertypeid ?? '',
  };

  // Edit dialog
  const [displayEditDialog, setDisplayEditDialog] = useState(false);
  const [editDialogTitle, setEditDialogTitle] = useState('');
  const [indexId, setIndexId] = useState('');
  const [studId, setStudId] = useState('');
  const [form, setForm] = useState<ExposureForm>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);

  // Photo/video uploads concern.
  const uploads = useExposureUploads({
    schoolId,
    getOwnerId: () => studId || String(schoolId),
    postExposureUploads,
    savedUploads,
    notify,
    showLoader,
    hideLoader,
  });

  const dialogs = useExposureDialogs({
    schoolId,
    savePayloadMeta,
    studentJsonData,
    allClassSectionData,
    getGroupName,
    refetchExposure,
    addExposureStudent,
    notify,
  });

  const remove = useRemoveStudent({
    schoolId,
    removeStudentLocally,
    refetchExposure,
    postExposureVisit,
    notify,
  });

  function setField<K extends keyof ExposureForm>(k: K, v: ExposureForm[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function handleCollegeChange(collegeId: string | number) {
    setForm((p) => ({ ...p, collegeName: collegeId }));
  }

  // District drives the college list; changing it resets the chosen college.
  function handleDistrictChange(districtId: string | number) {
    setForm((p) => ({ ...p, dist: districtId, collegeName: '' }));
  }

  const handleReasonChange = (value: number) =>
    setForm((p) => ({ ...p, reason: value, OtherReason: '' }));

  const handleVisitChange = (value: string) =>
    setForm({ ...EMPTY_FORM, exposureVisit: value });

  function onRowEdit(row: StudentRow) {
    setIndexId(row.IndxID || '');
    setStudId(row.user_id);
    setEditDialogTitle(`${row.name} - ${row.user_id}`);
    setSubmitted(false);
    const ctx = {
      collegeMaster,
      districtList,
      setCollegeOptions,
      setDistrictList,
    };
    setForm(buildEditForm(row, ctx));
    setDisplayEditDialog(true);
  }

  const isFormValid = () =>
    form.exposureVisit == '2' ||
    (form.exposureVisit == '1' &&
      !!form.dist &&
      !!form.collegeName &&
      !!form.visitDate &&
      !!form.teacher1);

  async function onSubmit() {
    setSubmitted(true);
    if (!isFormValid()) return;
    const ctx = { indexId, schoolId, studId, districtList };
    const payload = buildSavePayload(form, ctx);
    try {
      const res = await postExposureVisit(payload).unwrap();
      if (res.dataStatus) {
        notify('success', 'Success', res.message);
        refetchExposure();
        setDisplayEditDialog(false);
      } else notify('error', 'Error', res.message);
    } catch {
      notify('error', 'Error', 'Something went wrong. Please try again.');
    }
  }

  return {
    form,
    setField,
    submitted,
    editDialogTitle,
    displayEditDialog,
    setDisplayEditDialog,
    handleCollegeChange,
    handleReasonChange,
    handleVisitChange,
    onRowEdit,
    onSubmit,
    handleDistrictChange,
    ...dialogs,
    ...remove,
    ...uploads,
  };
}
